import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  Grid,
  LinearProgress,
  alpha,
  SxProps,
  Theme,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import { useLiveProgress } from "../hooks/useLiveProgress";
import { formatCurrency } from "../../../shared/utils";

export const KpiCards = () => {
  const {
    projectCount,
    portfolioValue,
    riskCount,
    avgProgress,
    isLoading,
    isError,
    error,
  } = useLiveProgress();

  const totalRiskProjects = riskCount.low + riskCount.medium + riskCount.high;

  if (isLoading) {
    return (
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Alert severity="error">
          {error || "Failed to load KPI data"}
        </Alert>
      </Box>
    );
  }

  const cardStyles: SxProps<Theme> = {
    backdropFilter: "blur(8px)",
    backgroundColor: (theme) =>
      theme.palette.mode === "dark"
        ? alpha(theme.palette.background.paper, 0.4)
        : alpha(theme.palette.background.paper, 0.8),
    boxShadow: (theme) => theme.shadows[15],
    height: "100%",
    display: "flex",
    flexDirection: "column",

    border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.05)}`,
  };
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
      }}
    >
      <Grid container spacing={2} alignItems="stretch">
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            variant="outlined"
            sx={{
              ...cardStyles,
            }}
          >
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                gap: 1.5,
              }}
            >
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <AccountTreeIcon color="primary" fontSize="medium" />
                  <Typography variant="subtitle1" color="text.secondary">
                    Total Projects
                  </Typography>
                </Stack>
                <Typography variant="h5" fontWeight="700">
                  {projectCount.total}
                </Typography>
              </Stack>
              <Divider />
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  <Chip
                    label={`${projectCount.completed} Completed`}
                    size="small"
                    variant="outlined"
                    color="success"
                  />
                  <Chip
                    label={`${projectCount.inProgress} In Progress`}
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                  <Chip
                    label={`${projectCount.planned} Planned`}
                    size="small"
                    variant="outlined"
                    color="warning"
                  />
                </Stack>
              </Stack>
            </Box>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            variant="outlined"
            sx={{
              ...cardStyles,
            }}
          >
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                gap: 1.5,
              }}
            >
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Stack direction="column" spacing={0.5}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AttachMoneyIcon color="success" fontSize="medium" />
                    <Typography variant="subtitle1" color="text.secondary">
                      Programme Value
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 4.5 }}>
                    {portfolioValue.totalPortfolio} portfolios
                  </Typography>
                </Stack>
                <Typography variant="h5" fontWeight="700">
                  {formatCurrency(portfolioValue.totalBudget)}
                </Typography>
              </Stack>
              <Divider />
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Spent: {formatCurrency(portfolioValue.totalSpent)}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color={
                      portfolioValue.totalRemaining < 0
                        ? "error.main"
                        : "success.main"
                    }
                  >
                    Remaining: {formatCurrency(portfolioValue.totalRemaining)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            variant="outlined"
            sx={{
              ...cardStyles,
            }}
          >
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                gap: 1.5,
              }}
            >
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <TrendingUpIcon color="info" fontSize="medium" />
                  <Typography variant="subtitle1" color="text.secondary">
                    Avg Progress
                  </Typography>
                </Stack>
                <Typography variant="h5" fontWeight="700">
                  {avgProgress.toFixed(1)}%
                </Typography>
              </Stack>
              <Divider />
              <Stack
                spacing={0.5}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ flex: 1 }}
                gap={2}
              >
                <Typography variant="caption" color="text.secondary">
                  Across all projects
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={avgProgress}
                  sx={{
                    flex: 1,
                    height: 4,
                    borderRadius: 1,
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "info.main",
                    },
                  }}
                />
              </Stack>
            </Box>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            variant="outlined"
            sx={{
              ...cardStyles,
            }}
          >
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                flex: 1,
                gap: 1.5,
              }}
            >
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <WarningIcon color="warning" fontSize="medium" />
                  <Typography variant="subtitle1" color="text.secondary">
                    At Risk Projects
                  </Typography>
                </Stack>
                <Typography variant="h5" fontWeight="700">
                  {totalRiskProjects}
                </Typography>
              </Stack>
              <Divider />
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Stack spacing={0.5} sx={{ flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    gap={0.5}
                  >
                    {riskCount.low > 0 && (
                      <Chip
                        label={`${riskCount.low} Low Risk`}
                        size="small"
                        variant="outlined"
                        color="success"
                      />
                    )}
                    {riskCount.medium > 0 && (
                      <Chip
                        label={`${riskCount.medium} Medium Risk`}
                        size="small"
                        variant="outlined"
                        color="warning"
                      />
                    )}
                    {riskCount.high > 0 && (
                      <Chip
                        label={`${riskCount.high} High Risk`}
                        size="small"
                        variant="outlined"
                        color="error"
                      />
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
